import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { url } from "../../api";
import { useGetStandaloneArtworkQuery } from "../../services/rtkApi/artistsGalleryApi";
import type { ArtistArtwork, WaybackInfo } from "../../services/rtkApi/artistsGalleryApi";

function fmtUTC(d: Date): string {
  return d.toUTCString();
}

function waybackLine(wb: WaybackInfo): React.ReactNode {
  const status = wb?.status || "queued";
  const captured = wb?.capturedAt ? new Date(wb.capturedAt) : null;
  const capOk = captured && !Number.isNaN(captured.getTime());

  if (status === "captured" && capOk) {
    const link = wb.snapshotUrl ? (
      <>
        {" "}
        (
        <a href={wb.snapshotUrl} target="_blank" rel="noopener noreferrer">
          view snapshot
        </a>
        )
      </>
    ) : null;
    return (
      <>
        The Internet Archive's Wayback Machine captured this URL on{" "}
        <time dateTime={captured!.toISOString()}>{fmtUTC(captured!)}</time>
        {link} as third-party validation that this artwork was hosted here on this date.
      </>
    );
  }

  if (status === "failed") {
    return (
      <>We queued this URL with the Wayback Machine, but a snapshot did not complete successfully in time.</>
    );
  }

  if (status === "skipped") {
    return <>This artwork was not submitted for Wayback archival.</>;
  }

  const mid =
    status === "requested"
      ? <> Snapshot request is <strong>in progress</strong>.</>
      : <> Snapshot capture is still <strong>pending</strong>.</>;

  return (
    <>
      The Internet Archive's Wayback Machine has not finalized an archive for this URL yet.
      {mid} When it succeeds, that capture becomes third-party validation that this artwork was hosted here on this date.
    </>
  );
}

function immutabilityLine(captured: boolean): string {
  if (captured) {
    return "After archival, changing the artwork file behind this permalink would contradict what the archived page records. Wayback snapshots keep this HTML as served, including the SHA-256 of the image bytes below (visible in snapshot source).";
  }
  return "The SHA-256 below fingerprints the raw image linked from this page. After Wayback archives this URL, the snapshot will freeze that wording and hash together so the file cannot silently diverge without breaking with the archival record (the archived HTML includes these lines verbatim).";
}

const ArtworkStandalonePage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { data: artwork, isLoading, error } = useGetStandaloneArtworkQuery(id, {
    skip: !id,
  });

  // robots meta: respect standalonePageEnabled flag — when off, hide from indexers
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = artwork?.standalonePageEnabled ? "all" : "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [artwork?.standalonePageEnabled]);

  useEffect(() => {
    if (artwork) {
      const display = artwork.artist?.displayName || artwork.artist?.username || "artwork";
      document.title = `${artwork.title || "untitled"} · ${display} · n450s.com`;
    }
  }, [artwork]);

  if (!id) {
    return (
      <Page>
        <Main><h1>missing artwork id</h1></Main>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <Main><h1>loading…</h1></Main>
      </Page>
    );
  }

  if (error || !artwork) {
    return (
      <Page>
        <Main>
          <h1>404 — artwork not found</h1>
          <p><Link to="/art">→ back to gallery</Link></p>
        </Main>
      </Page>
    );
  }

  return (
    <ArtworkBody artwork={artwork} />
  );
};

const ArtworkBody: React.FC<{ artwork: ArtistArtwork }> = ({ artwork }) => {
  const artist = artwork.artist;
  const display = artist?.displayName || artist?.username || "unknown";
  const username = artist?.username || "unknown";
  const dateMade = artwork.dateMade ? new Date(artwork.dateMade) : null;
  const dateMadeOk = dateMade && !Number.isNaN(dateMade.getTime());
  const dateAdded = new Date(artwork.dateAdded);
  const imageSrc = artwork.imageFilename
    ? `${url}/api/artists-gallery/images/${encodeURIComponent(artwork.imageFilename)}`
    : "";
  const wb = artwork.wayback || ({} as WaybackInfo);
  const captured = wb.status === "captured" && !!wb.capturedAt;

  return (
    <Page>
      <LogoBand>
        <Link to="/" title="n450s.com">
          <Logo src="/bolt-glow.png" alt="n450s.com" />
        </Link>
      </LogoBand>
      <Main>
        <Title>{artwork.title || "untitled"}</Title>
        <Meta>
          <span>
            by <strong>{display}</strong> (@{username})
          </span>
          {dateMadeOk && (
            <span>
              · <time dateTime={dateMade!.toISOString()}>{fmtUTC(dateMade!)}</time>
            </span>
          )}
          {artwork.donation && <Badge>donation</Badge>}
        </Meta>
        {imageSrc && (
          <ImageWrap>
            <img src={imageSrc} alt={artwork.title || "untitled"} />
          </ImageWrap>
        )}
        {artwork.description && <Description>{artwork.description}</Description>}
        <Footer>
          <p>
            This artwork was uploaded to <Link to="/">n450s.com</Link> on{" "}
            <Timestamp dateTime={dateAdded.toISOString()}>{fmtUTC(dateAdded)}</Timestamp>.
          </p>
          <p>This standalone page is intended as a permanent reference. {waybackLine(wb)}</p>
          <p>{immutabilityLine(captured)}</p>
          {artwork.imageContentSha256 && artwork.imageContentSha256.length >= 64 && (
            <HashLine>
              <strong>Image SHA-256 (hex):</strong>
              <br />
              <code>{artwork.imageContentSha256}</code>
            </HashLine>
          )}
          <p>
            <Link to={`/art/${encodeURIComponent(username)}`}>
              → view {display}'s gallery on n450s.com
            </Link>
          </p>
        </Footer>
      </Main>
    </Page>
  );
};

const Page = styled.div`
  background: #f4f0ea;
  color: #221c14;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.5;
`;

const LogoBand = styled.div`
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 18px 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  a {
    display: inline-block;
    line-height: 0;
  }
`;

const Logo = styled.img`
  height: 44px;
  width: auto;
  display: block;
`;

const Main = styled.main`
  max-width: 880px;
  margin: 32px auto 80px;
  padding: 0 24px;
`;

const Title = styled.h1`
  margin: 0 0 6px 0;
  font-size: 1.6rem;
`;

const Meta = styled.div`
  font-size: 0.9rem;
  opacity: 0.75;
  margin-bottom: 24px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border: 1px solid #82d3a2;
  background: #d6f5e1;
  color: #0a5b2e;
`;

const ImageWrap = styled.div`
  background: #ffffff;
  padding: 18px;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(20, 18, 12, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;

  img {
    max-width: 100%;
    max-height: 78vh;
    border-radius: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
`;

const Description = styled.p`
  white-space: pre-wrap;
  font-size: 1rem;
`;

const Footer = styled.footer`
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.85rem;
  opacity: 0.88;
  line-height: 1.45;

  p {
    margin: 0.65em 0;
  }

  a {
    color: #221c14;
  }
`;

const Timestamp = styled.time`
  font-variant-numeric: tabular-nums;
  font-size: 0.78rem;
`;

const HashLine = styled.p`
  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.74rem;
    word-break: break-all;
    display: inline-block;
    max-width: 100%;
    margin-top: 4px;
  }
`;

export default ArtworkStandalonePage;
